import { OrderItemV2ExtAdd } from "@/types/inventoryV2";
import { DatePicker, Divider, Form, Input, InputNumber, Modal, Select } from "antd";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import Dict from "@/components/Dict";
import { DictEnum } from "@/types/dict";
import { CountrySelect } from "@/components/Selector";

interface EditExtProps {
  open: boolean;
  data?: OrderItemV2ExtAdd;
  onConfirm: (ext?: OrderItemV2ExtAdd) => void;
  onClose: () => void;
}


const EditExt: FC<EditExtProps> = ({ open, data, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      if (data) {
        form.setFieldsValue({
          originalBrand: data.originalBrand?.split(','),
          originalCode: data.originalCode,
          manufactureDate: data.manufactureDate ? dayjs(data.manufactureDate) : undefined,
          msdLevel: data.msdLevel,
          originPlace: data.originPlace,
          netWeight: data.netWeight,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, data, form]);

  const onFinish = (values: OrderItemV2ExtAdd & { originalBrand: string[] }) => {
    const hasValue =
      values.originalBrand?.length > 0 ||
      values.originalCode ||
      values.manufactureDate ||
      values.msdLevel ||
      values.originPlace ||
      values.netWeight;

    if (!hasValue) {
      onConfirm(undefined);
    } else {
      const ext: OrderItemV2ExtAdd = {
        originalBrand: values.originalBrand?.join(',') || '',
        originalCode: values.originalCode || '',
        manufactureDate: values.manufactureDate
          ? dayjs(values.manufactureDate).endOf('day').valueOf()
          : undefined,
        msdLevel: values.msdLevel || '',
        originPlace: values.originPlace || '',
        netWeight: values.netWeight || 0,
      };
      onConfirm(ext);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={t('purchase.otherInfo')}
      open={open}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      width={500}
      destroyOnClose
    >
      <Form form={form} onFinish={onFinish}>
        <Form.Item
          name="originalBrand"
          label={t('purchase.originalBrand')}
          style={{ marginBottom: 12 }}
        >
          <Dict type={DictEnum.BRAND} mode="multiple" placeholder={t('purchase.plOriginalBrand')} />
        </Form.Item>
        <Form.Item
          name="originalCode"
          label={t('purchase.originalCode')}
          style={{ marginBottom: 12 }}
        >
          <Input placeholder={t('purchase.plOriginalCode')} />
        </Form.Item>
        <Form.Item
          name="manufactureDate"
          label={t('purchase.manufactureDate')}
          style={{ marginBottom: 12 }}
        >
          <DatePicker placeholder={t('purchase.plManufactureDate')} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="msdLevel"
          label={t('purchase.msdLevel')}
          style={{ marginBottom: 12 }}
        >
          <Select
            placeholder={t('purchase.plMsdLevel')}
            allowClear
            options={[
              { label: 'MSL 1', value: 'MSL 1' },
              { label: 'MSL 2', value: 'MSL 2' },
              { label: 'MSL 2a', value: 'MSL 2a' },
              { label: 'MSL 3', value: 'MSL 3' },
              { label: 'MSL 4', value: 'MSL 4' },
              { label: 'MSL 5', value: 'MSL 5' },
              { label: 'MSL 5a', value: 'MSL 5a' },
              { label: 'MSL 6', value: 'MSL 6' },
            ]}
          />
        </Form.Item>
        <Form.Item
          name="originPlace"
          label={t('purchase.originPlace')}
          style={{ marginBottom: 12 }}
        >
          <CountrySelect placeholder={t('purchase.plOriginPlace')} />
        </Form.Item>
        <Form.Item
          name="netWeight"
          label={t('purchase.netWeight')}
          style={{ marginBottom: 12 }}
        >
          <InputNumber changeOnWheel placeholder={t('purchase.plNetWeight')} style={{ width: '100%' }} addonAfter="g" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditExt;